import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  SvgIcon,
  Menu,
  MenuItem,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import PostAddIcon from "@material-ui/icons/PostAdd";
import GetAppIcon from "@material-ui/icons/GetApp";
import PaletteIcon from "@material-ui/icons/Palette";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../config/routes";
import { useAuth } from "../../config/AuthContext";
import { ReactComponent as LogoIcon } from "../../blogLogo.svg";

const useStyles = makeStyles((theme) => ({
  appbar: {
    backgroundColor: theme.palette.primary.dark,
  },
  toolbar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    "& .MuiButton-root": {
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.contrastText,
    },
    "& .MuiIconButton-root": {
      color: theme.palette.primary.contrastText,
      "& .MuiSvgIcon-root": {
        fontSize: theme.spacing(4),
      },
    },
    "& .MuiTypography-root": {
      color: theme.palette.primary.contrastText,
    },
  },
  menu: {
    "& .MuiPopover-paper": {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "& .MuiMenuItem-root": {
        cursor: "default",
      },

      "& .MuiButton-root": {
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.contrastText,
      },
    },
  },
}));

const Header = ({ setDarkMode }) => {
  const {
    signInWithGoogle,
    signOutFromGoogle,
    currentUser,
    moderator,
  } = useAuth();
  const history = useHistory();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showA2HSmsg, setShowA2HSmsg] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  const handleSignIn = async () => {
    await signInWithGoogle()
      .then(() => {
        //console.log("User has just logged in");
        setAnchorEl(null);
      })
      .catch((err) => {
        console.log(
          `Error occured while trying to log you in : ${err.message}`
        );
      });
  };

  const handleWritePost = () => {
    history.push(ROUTES.ADMIN);
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    await signOutFromGoogle()
      .then(() => {
        //  console.log("User has just logged out");
        setAnchorEl(null);
      })
      .catch((err) => {
        console.log(`There was an error while logging you out ${err.message}`);
      });
  };

  const handleInstallApp = () => {
    setAnchorEl(null);
    if (!deferredPrompt) {
      setShowA2HSmsg(true);
      return;
    }
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the A2HS prompt");
      } else {
        console.log("User dismissed the A2HS prompt");
      }
      setDeferredPrompt(null);
    });
  };

  return (
    <AppBar position="sticky" className={classes.appbar}>
      <Toolbar className={classes.toolbar}>
        <IconButton aria-label="menu" onClick={() => history.push(ROUTES.HOME)}>
          <SvgIcon
            component={LogoIcon}
            fontSize="large"
            viewBox="0 0 512 512"
          />
        </IconButton>
        <Typography component="h6" variant="h6">
          Maadhyam
        </Typography>

        <IconButton
          aria-label="menu-icon"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {!anchorEl && <MenuIcon />}
          {anchorEl && <CloseIcon />}
        </IconButton>

        <Menu
          className={classes.menu}
          id="simple-menu"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {!currentUser && (
            <div>
              <MenuItem disableRipple>
                <Typography>Welcome, Guest</Typography>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<LockOpenIcon />} onClick={handleSignIn}>
                  Login
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<GetAppIcon />} onClick={handleInstallApp}>
                  Install App
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  startIcon={<PaletteIcon />}
                  onClick={() => {
                    setDarkMode((prev) => !prev);
                    setAnchorEl(null);
                  }}
                >
                  Change Theme
                </Button>
              </MenuItem>
            </div>
          )}

          {currentUser && !moderator && (
            <div>
              <MenuItem disableRipple>
                <Typography>{`Welcome, ${currentUser.displayName}`}</Typography>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<GetAppIcon />} onClick={handleInstallApp}>
                  Install App
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  startIcon={<PaletteIcon />}
                  onClick={() => {
                    setDarkMode((prev) => !prev);
                    setAnchorEl(null);
                  }}
                >
                  Change Theme
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<LockIcon />} onClick={handleSignOut}>
                  Logout
                </Button>
              </MenuItem>
            </div>
          )}

          {currentUser && moderator && (
            <div>
              <MenuItem disableRipple>
                <Typography>{`Welcome, ${currentUser.displayName}`}</Typography>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<PostAddIcon />} onClick={handleWritePost}>
                  Write a Post
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button startIcon={<GetAppIcon />} onClick={handleInstallApp}>
                  Install App
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  startIcon={<PaletteIcon />}
                  onClick={() => {
                    setDarkMode((prev) => !prev);
                    setAnchorEl(null);
                  }}
                >
                  Change Theme
                </Button>
              </MenuItem>

              <MenuItem disableRipple>
                <Button startIcon={<LockIcon />} onClick={handleSignOut}>
                  Logout
                </Button>
              </MenuItem>
            </div>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;

// {showA2HSmsg && (
//   <General
//     setShowGeneral={setShowA2HSmsg}
//     headline={`App Installation Information`}
//     content={`If you're seeing this message it means one of the following: 1) Your browser/device does not support the pwa installation.
//   Supported browsers are Chrome on Android, Chrome on Windows. 2) App is already installed on your device. `}
//   />
// )}
